import React, { useState, useEffect, useCallback, useRef } from 'react';
// Phase 3: State-based overlay approach (no routing dependencies)
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
// Note: Not using Radix Tabs - using custom buttons for full intercept control
import { Separator } from './ui/separator';
import UnsavedChangesModal from './ui/UnsavedChangesModal';

// Icons
import { ArrowLeft, Save, X, AlertTriangle, CheckCircle, Package, Trophy } from 'lucide-react';

// Import the existing ProductMasterDetail component for tab content
import ProductMasterDetail from './ProductMasterDetail';
import ProductPricingV2 from './ProductPricingV2';
import BOMManager from './product/BOMManager';
import PricingTab from './product/PricingTab';
import CompetitionTab from './product/CompetitionTab';

// Use runtime environment config instead of env variables
import { config } from '../config/environment';

const API = config.apiBaseUrl;
const MOCK_TENANT_ID = 'tenant-123';

/**
 * Full-Page Product Detail Overlay
 * Phase 3: State-based overlay (no routing dependencies)
 */
const ProductDetailPage = ({ 
  open = false, 
  onClose, 
  productId, 
  initialTab = 'general' 
}) => {
  // State management
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false); // Don't start in loading state
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Track which tab has unsaved changes (for PricingTab modal)
  const [pricingTabDirty, setPricingTabDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null); // {type: 'tab'|'close'|'product', target: value}
  
  // Tab management with state persistence - Performance optimized
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // CRITICAL FIX: Create ref to access child component's save function
  const productDetailRef = useRef(null);
  
  // Ref to access PricingTab's save function
  const pricingTabSaveRef = useRef(null);
  
  // Handle tab switch with unsaved changes check
  const handleTabSwitch = useCallback((newTab) => {
    console.log('🔄🔄🔄 handleTabSwitch CALLED 🔄🔄🔄');
    console.log('🔄 Parameters:', { newTab, activeTab, pricingTabDirty });
    
    // Check if current tab is pricing and has unsaved changes
    if (pricingTabDirty && activeTab === 'pricing' && newTab !== 'pricing') {
      console.log('🛑🛑🛑 INTERCEPTED - blocking tab switch 🛑🛑🛑');
      setPendingNavigation({ type: 'tab', target: newTab });
      setShowUnsavedModal(true);
      return; // BLOCK the switch
    }
    
    console.log('✅ Allowing tab switch to:', newTab);
    setActiveTab(newTab);
  }, [activeTab, pricingTabDirty]);
  
  // Handle close with unsaved changes check
  const handleClose = useCallback(() => {
    if (pricingTabDirty && activeTab === 'pricing') {
      setPendingNavigation({ type: 'close' });
      setShowUnsavedModal(true);
      return;
    }
    onClose?.();
  }, [pricingTabDirty, activeTab, onClose]);
  
  // Handle "Leave anyway" from modal
  const handleDiscardChanges = useCallback(() => {
    setPricingTabDirty(false);
    setShowUnsavedModal(false);
    
    if (pendingNavigation?.type === 'tab') {
      setActiveTab(pendingNavigation.target);
    } else if (pendingNavigation?.type === 'close') {
      onClose?.();
    }
    setPendingNavigation(null);
  }, [pendingNavigation, onClose]);
  
  // Handle "Stay & Save" from modal - trigger save and proceed with navigation
  const handleStayAndSave = useCallback(async () => {
    setShowUnsavedModal(false);
    
    // Try to trigger save on PricingTab via ref
    if (pricingTabSaveRef.current?.triggerSave) {
      try {
        setSaving(true);
        await pricingTabSaveRef.current.triggerSave();
        setPricingTabDirty(false);
        
        // After successful save, proceed with pending navigation
        if (pendingNavigation?.type === 'tab') {
          setActiveTab(pendingNavigation.target);
        } else if (pendingNavigation?.type === 'close') {
          onClose?.();
        }
        setPendingNavigation(null);
      } catch (error) {
        console.error('Failed to save:', error);
        toast.error('Failed to save changes. Please try again.');
      } finally {
        setSaving(false);
      }
    } else {
      // Fallback: Just close modal and let user manually save
      toast.info('Please click outside the input field to save your changes');
      setPendingNavigation(null);
    }
  }, [pendingNavigation, onClose]);
  
  // Callback for PricingTab dirty state changes
  const handlePricingDirtyChange = useCallback((isDirty) => {
    console.log('📥 ProductDetailPage received pricingTabDirty:', isDirty);
    setPricingTabDirty(isDirty);
  }, []);
  
  // Callback for PricingTab save complete
  const handlePricingSaveComplete = useCallback(() => {
    setPricingTabDirty(false);
  }, []);
  
  // Performance monitoring for overlay operations
  useEffect(() => {
    if (open && productId) {
      const overlayStartTime = performance.now();
      
      // Monitor overlay open performance
      const timeoutId = setTimeout(() => {
        const overlayEndTime = performance.now();
        const openTime = overlayEndTime - overlayStartTime;
        console.log(`🎯 Overlay open performance: ${openTime.toFixed(2)}ms (target: ≤400ms)`);
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [open, productId]);

  // Available tabs for product detail - UPDATED FOR BOM/COSTING SYSTEM
  const productType = product?.product_type || 'finished_product';
  const isRawMaterial = productType === 'raw_material';
  
  const tabs = [
    { id: 'general', label: 'General', icon: Package },
    { id: 'purchasing', label: 'Purchasing', icon: Package },
    { id: 'sales', label: 'Sales', icon: Package },
    { id: 'inventory', label: 'Inventory', icon: Package },
    { id: 'channels', label: 'Channels', icon: Package },
    // BOM tab - only for finished products
    ...(!isRawMaterial ? [{ id: 'bom', label: 'BOM', icon: Package }] : []),
    { id: 'pricing', label: 'Pricing', icon: Package },
    { id: 'competition', label: 'Competition', icon: Trophy },
    { id: 'suppliers', label: 'Suppliers', icon: Package }
  ];

  // Load product data
  const loadProduct = useCallback(async () => {
    if (!productId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 ProductDetailPage: Fetching product data for ID:', productId);
      const response = await axios.get(`${API}/product-master/item/${productId}/details`, {
        params: { tenant_id: MOCK_TENANT_ID }
      });
      
      console.log('📦 ProductDetailPage: API Response:', response.data);
      
      if (response.data) {
        // Handle both wrapped and unwrapped responses
        const productData = response.data.item || response.data.data || response.data;
        console.log('✅ ProductDetailPage: Setting product data:', productData);
        setProduct(productData);
      } else {
        console.error('❌ ProductDetailPage: No data in response');
        setError('Product not found');
      }
    } catch (err) {
      console.error('❌ ProductDetailPage: Error loading product:', err);
      setError(err.response?.data?.detail || 'Failed to load product');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  // Load product when overlay opens or productId changes
  useEffect(() => {
    if (open && productId) {
      console.log('ProductDetailPage: Loading product for ID:', productId);
      setError(null);
      loadProduct();
    } else if (open && !productId) {
      console.log('ProductDetailPage: No productId provided');
      setError('No product ID provided');
    } else if (!open) {
      // Reset state when overlay closes
      setProduct(null);
      setError(null);
      setLoading(false);
    }
  }, [open, productId, loadProduct]);
  
  // CRITICAL FIX: Listen for product updates and reload data
  useEffect(() => {
    const handleProductUpdated = (event) => {
      console.log('🔔 ProductDetailPage: Received productUpdated event:', event.detail);
      if (event.detail && event.detail.id === productId) {
        console.log('🔄 ProductDetailPage: Reloading product data after save');
        loadProduct(); // Reload the product from database
      }
    };
    
    window.addEventListener('productUpdated', handleProductUpdated);
    return () => window.removeEventListener('productUpdated', handleProductUpdated);
  }, [productId, loadProduct]);

  // Handle closing the overlay
  const handleBack = useCallback(() => {
    console.log('🔙 handleBack called:', { activeTab, pricingTabDirty, hasUnsavedChanges });
    
    // Check for PricingTab unsaved changes FIRST
    if (activeTab === 'pricing' && pricingTabDirty) {
      console.log('⚠️ BLOCKING CLOSE - showing modal');
      setPendingNavigation({ type: 'close' });
      setShowUnsavedModal(true);
      return;
    }
    
    // Legacy check for other tabs' unsaved changes
    if (hasUnsavedChanges) {
      if (!confirm('You have unsaved changes. Are you sure you want to leave?')) {
        return;
      }
    }
    setActiveTab('general'); // Reset tab for next open
    onClose && onClose();
  }, [onClose, hasUnsavedChanges, activeTab, pricingTabDirty]);

  // CRITICAL FIX: Handle save all changes by calling child component's save function
  const handleSaveAll = useCallback(async () => {
    console.log('🔵 PARENT: Save All button clicked');
    console.log('🔵 PARENT: hasUnsavedChanges =', hasUnsavedChanges);
    console.log('🔵 PARENT: saving =', saving);
    console.log('🔵 PARENT: activeTab =', activeTab);
    console.log('🔵 PARENT: productDetailRef.current =', productDetailRef.current);
    
    // For pricing and bom tabs, save the product directly since they don't use ProductMasterDetail
    if (activeTab === 'pricing' || activeTab === 'bom') {
      if (!productId || !product) {
        toast.error('Unable to save: No product loaded');
        return;
      }
      
      setSaving(true);
      try {
        console.log('🔵 PARENT: Saving product directly for', activeTab, 'tab');
        await axios.patch(`${API}/product-master/item/${productId}`, product, {
          params: { tenant_id: MOCK_TENANT_ID }
        });
        console.log('✅ PARENT: Product saved successfully');
        setHasUnsavedChanges(false);
        toast.success('Product saved successfully');
      } catch (error) {
        console.error('❌ PARENT: Save error:', error);
        toast.error(error.response?.data?.detail || 'Failed to save product');
      } finally {
        setSaving(false);
      }
      return;
    }
    
    // For other tabs, use the ProductMasterDetail ref
    if (!productDetailRef.current) {
      console.error('❌ PARENT: productDetailRef.current is null!');
      toast.error('Unable to save: Component not ready');
      return;
    }
    
    setSaving(true);
    try {
      console.log('🔵 PARENT: Calling child handleSave()');
      // Call the child component's actual save function
      await productDetailRef.current.handleSave();
      console.log('✅ PARENT: Child handleSave() completed');
      setHasUnsavedChanges(false);
      // toast.success is already handled in the child component
    } catch (error) {
      console.error('❌ PARENT: Save error:', error);
      // toast.error is already handled in the child component
    } finally {
      setSaving(false);
    }
  }, [hasUnsavedChanges, saving, activeTab, productId, product]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (hasUnsavedChanges) {
          handleSaveAll();
        }
      }
      
      // Escape to go back
      if (e.key === 'Escape') {
        handleBack();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasUnsavedChanges, handleSaveAll, handleBack]);

  // Loading state - show within overlay with proper fixed positioning
  if (loading) {
    return (
      <div className="fixed inset-0 z-[1000] bg-slate-900 overflow-y-auto"
           style={{ display: 'block', position: 'fixed' }}>
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-300">Loading product details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show loading skeleton during initial load
  if (loading && !product) {
    return (
      <div className="fixed inset-0 z-[1000] bg-gray-50 overflow-y-auto">
        <div className="sticky top-0 z-50 bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Product Master
                </Button>
                <span className="text-gray-400">/</span>
                <span className="font-medium text-gray-500">Loading...</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="text-gray-500 text-sm">Loading product details...</span>
          </div>
        </div>
      </div>
    );
  }

  // Use actual product data (no mock fallback)
  const displayProduct = product || {};

  // Error state - show within overlay
  if (error || (!loading && !product)) {
    return (
      <div className="h-full flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Product Not Found</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={handleBack} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Product Master
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Don't render if not open
  if (!open) {
    return null;
  }

  // Main render - Full-screen overlay with proper z-index
  return (
    <div className="fixed inset-0 z-[1000] bg-slate-900 overflow-y-auto"
         style={{ display: 'block', position: 'fixed' }}
    >
      {/* Compact Sticky Header */}
      <div className="sticky top-0 z-50 bg-[#0d1b2a] border-b border-gray-800 shadow-sm">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between py-2">
            {/* Compact Breadcrumb Navigation */}
            <div className="flex items-center gap-2 text-xs">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="text-gray-400 hover:text-white h-7 px-2"
              >
                <ArrowLeft className="w-3 h-3 mr-1" />
                Product Master
              </Button>
              <span className="text-gray-600">/</span>
              <span className="font-medium text-white truncate max-w-[200px]">
                {displayProduct?.name || displayProduct?.sku || 'Product'}
              </span>
              <span className="text-gray-600">/</span>
              <span className="text-gray-400 capitalize text-[11px]">
                {tabs.find(t => t.id === activeTab)?.label || activeTab}
              </span>
            </div>

            {/* Compact Action Buttons */}
            <div className="flex items-center gap-2">
              {hasUnsavedChanges && (
                <span className="text-orange-400 text-[10px] uppercase font-medium">
                  Unsaved
                </span>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                disabled={saving}
                className="h-7 px-3 text-[11px] text-gray-400 hover:text-white"
              >
                <X className="w-3 h-3 mr-1" />
                Cancel
              </Button>
              
              <Button
                size="sm"
                onClick={() => {
                  console.log('🔵 BUTTON CLICKED: Save All button onClick fired');
                  handleSaveAll();
                }}
                disabled={!hasUnsavedChanges || saving}
                className="h-7 px-3 text-[11px] bg-blue-600 hover:bg-blue-700 text-white"
              >
                {saving ? (
                  <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full mr-1" />
                ) : (
                  <Save className="w-3 h-3 mr-1" />
                )}
                {saving ? 'Saving...' : 'Save All'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content - Dark theme, compact */}
      <div className="h-full overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 py-3">
        
        {/* Compact Product Header Card */}
        <div className="bg-[#1a2744] rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-white text-sm font-medium truncate max-w-md">
                  {displayProduct?.name || 'Unknown Product'}
                </h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-gray-400 uppercase">SKU</span>
                  <span className="text-xs font-bold text-blue-400">
                    {displayProduct?.sku || displayProduct?.sku_root || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
            
            <span className={`px-2 py-0.5 text-[10px] font-medium uppercase rounded ${
              displayProduct?.status === 'active' 
                ? 'bg-green-900/50 text-green-400' 
                : 'bg-gray-700 text-gray-400'
            }`}>
              {displayProduct?.status || 'Unknown'}
            </span>
          </div>
        </div>

        {/* Compact Tab Navigation - CUSTOM IMPLEMENTATION (not Radix) to allow interception */}
        <div className="mb-3">
          <div className="bg-[#0d1b2a] border border-gray-800 p-0.5 rounded-lg inline-flex">
            {tabs.map(tab => {
              console.log('🆕 NEW TAB BAR RENDERED - tab:', tab.id);
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    console.log('🔘 TAB BUTTON CLICKED:', tab.id);
                    console.log('🔘 Current state:', { activeTab, pricingTabDirty });
                    handleTabSwitch(tab.id);
                  }}
                  className={`text-[11px] py-1.5 px-3 rounded-md transition-colors ${
                    activeTab === tab.id
                      ? 'bg-[#1a2744] text-blue-400'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                  data-testid={`tab-trigger-${tab.id}`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content - Compact dark theme */}
          {tabs.map(tab => (
            <div key={tab.id} className={`mt-3 ${activeTab === tab.id ? 'block' : 'hidden'}`}>
                {activeTab === tab.id && (
                  <div className="bg-[#1a2744] rounded-lg p-3">
                      {/* BOM Tab - Bill of Materials Manager */}
                      {tab.id === 'bom' ? (
                        <BOMManager
                          productId={productId}
                          productType={product?.product_type || 'finished_product'}
                          onCostUpdate={() => {
                            loadProduct(); // Refresh product data when costs change
                          }}
                        />
                      ) : tab.id === 'pricing' ? (
                        /* New Pricing Tab with BOM-integrated costs */
                        <PricingTab
                          ref={pricingTabSaveRef}
                          productId={productId}
                          productData={product || {}}
                          onFieldChange={(field, value) => {
                            setProduct(prev => ({
                              ...prev,
                              [field]: value
                            }));
                            setHasUnsavedChanges(true);
                          }}
                          onNavigateToBOM={() => handleTabSwitch('bom')}
                          onDirtyChange={handlePricingDirtyChange}
                          onSaveComplete={handlePricingSaveComplete}
                        />
                      ) : tab.id === 'competition' ? (
                        /* Competition Tab - Competitive Analysis */
                        <CompetitionTab
                          productId={productId}
                          productData={product || {}}
                          onFieldChange={(field, value) => {
                            setProduct(prev => ({
                              ...prev,
                              [field]: value
                            }));
                            setHasUnsavedChanges(true);
                          }}
                        />
                      ) : (
                        /* Use existing ProductMasterDetail component for other tabs */
                        <ProductMasterDetail
                          ref={productDetailRef}
                          productId={productId}
                          item={displayProduct}
                          activeTab={tab.id}
                          onDataChange={() => setHasUnsavedChanges(true)}
                          fullPageMode={true}
                        />
                      )}
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
      </div>
      
      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        onDiscard={handleDiscardChanges}
        onSave={handleStayAndSave}
        title="Unsaved Changes"
        message="You have unsaved changes in the Pricing tab. What would you like to do?"
      />
    </div>
  );
};

export default ProductDetailPage;